import React, { Component } from 'react';
import { Link } from 'react-router-dom'
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formatPhoneNumber } from '../../common/lib/formatting';
import { Button, Tag } from 'antd';
import Portlet from '../../common/components/Portlet';
import PaginatedTable from '../../common/components/Table/PaginatedTable';
import { USERS_TABLE_LIMIT } from '../../redux/constants';
import BULK_ACTIONS_MAP, {
  CANCEL_DELETION,
  PURGE,
} from '../../redux/constants/bulk_actions'
import {
  setIsBulkUserWorkflow,
  fetchUsers,
  selectUserForBulk,
  deselectUserForBulk,
  startBulkActionWorkflow,
} from '../../redux/actions';
import { hasSelectedUsersSelector, selectedUserIdsSelector } from '../../redux/selectors/bulkActionsSelector';
import BulkActionsWorkflowModal from '../../common/components/Modal/BulkActionsWorkflow'
import UsersSearch from '../../common/components/Searchbar/UsersSearch'
import CSVDownloadButton from '../../common/components/CSVDownload/CSVDownloadButton';

import './index.css';

const mapStateToProps = (state) => ({
  searchParams: state.usersReducer.searchParams,
  users: state.usersReducer.users,
  isAdmin: state.loginReducer.isAdmin,
  totalCount: state.usersReducer.totalCount,
  limit: state.usersReducer.limit,
  markedForDeletion: state.usersReducer.markedForDeletion,
  errorDetail: state.usersReducer.errorDetail,
  isFetching: state.usersReducer.isFetching,
  offset: state.usersReducer.offset,
  selectedUserIds: selectedUserIdsSelector(state),
  isBulkActionsEnabled: hasSelectedUsersSelector(state)
});

const mapDispatchToProps = {
  fetchUsers,
  setIsBulkUserWorkflow,
  selectUserForBulk,
  deselectUserForBulk,
  startBulkActionWorkflow,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  currentPage: (stateProps.offset / stateProps.limit) + 1,
  getPage(page) {
    const {
      limit,
      markedForDeletion,
      searchParams,
    } = stateProps
    const zeroBased = page - 1
    const offset = zeroBased * limit
    dispatchProps.fetchUsers({
      limit,
      markedForDeletion,
      offset,
      searchParams,
    })
    },

    sortColumns(pagination, filters, sorter, extra){
      const {
        limit,
        markedForDeletion,
        searchParams,
      } = stateProps
      const offset = (pagination.current - 1) * limit;
      const column = sorter.field;
      const order = sorter.order;
      dispatchProps.fetchUsers({
        limit,
        markedForDeletion,
        offset,
        searchParams,
        column,
        order
      })
  },
})
@connect(mapStateToProps, mapDispatchToProps, mergeProps)
class UsersView extends Component {
  constructor(props) {
    super(props);
    this.handleSelectUsers = this.handleSelectUsers.bind(this);
    this.handleDeselectUsers = this.handleDeselectUsers.bind(this);
    this.startBulkActionWorkflow = this.startBulkActionWorkflow.bind(this)
  }

  componentDidMount() {
    const {
      searchParams,
      limit,
      markedForDeletion,
      setIsBulkUserWorkflow,
    } = this.props;
    // Make it so the selected users array gets cleared upon finishing workflow.
    // Required so that we can reuse bulk for single user page
    setIsBulkUserWorkflow()
  }

  handleSelectUsers(userId) {
    const { selectUserForBulk } = this.props;
    selectUserForBulk({ userId });
  }

  handleDeselectUsers(userId) {
    const { deselectUserForBulk } = this.props;
    deselectUserForBulk({ userId });
  }

  startBulkActionWorkflow(bulkActionType) {
    const { startBulkActionWorkflow } = this.props
    startBulkActionWorkflow({ bulkActionType })
  }

  render() {
    const {
      totalCount,
      isAdmin,
      users,
      getPage,
      sortColumns,
      currentPage,
      errorDetail,
      isFetching,
      offset,
      selectedUserIds,
      isBulkActionsEnabled,
      searchParams
    } = this.props

    const bulkActionsConfig = [
      // TODO: reinstate isAdmin check after it's verifiable w/ admin account
      ...(isAdmin ? [PURGE] : []),
      CANCEL_DELETION,
    ].map((key) => ({
      key,
      label: BULK_ACTIONS_MAP[key].ctaText,
    })).reverse();

    const portletConfig = {
      title: "PENDING DELETION USER LIST",
      actionsLabel: "Actions",
      actions: [],
      shouldShowBulkActions: false,
      renderSearchbar: () => (
        <UsersSearch shouldGetMarkedUsers />
      ),
      bulkActions: bulkActionsConfig,
      bulkSubmit: isBulkActionsEnabled && this.startBulkActionWorkflow,
    };

    const rowSelection = {
      hideSelectAll: false,
      selectedRowKeys: selectedUserIds,
      type: 'checkbox',
      onSelect: (user, selected) => {
        selected
          ? this.handleSelectUsers(user.sub)
          : this.handleDeselectUsers(user.sub);
      },
      onSelectAll: (user)=>{
        const shouldDeselect = selectedUserIds.length === users.length
        users.forEach(selectedUser => this[shouldDeselect ? 'handleDeselectUsers' : 'handleSelectUsers'](selectedUser.sub)) // Deselect or add them all
      },
      getCheckboxProps: ({ sub }) => ({
        disabled:
        selectedUserIds.length >= USERS_TABLE_LIMIT && !selectedUserIds.includes(sub),
        name: sub,
      }),
    };

    const columns = [
      {
        title: 'First Name',
        dataIndex: 'given_name',
        sorter: (a, b) =>  (a.given_name && b.given_name) ? a.given_name.localeCompare(b.given_name) : -1,
      },
      {
        title: 'Last Name',
        dataIndex: 'family_name',
        sorter: (a, b) =>  (a.family_name && b.family_name) ? a.family_name.localeCompare(b.family_name) : -1,
      },
      {
        title: 'Address',
        dataIndex: 'address',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: (a, b) =>  (a.email && b.email) ? a.email.localeCompare(b.email) : -1,
      },
      {
        title: 'Date Joined',
        dataIndex: 'created_at',
        render: (date) => moment(date).format('MMMM, D YYYY'),
        sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix()
      },
      {
        title: 'Phone #',
        dataIndex: 'phone_number',
        render: (number) => formatPhoneNumber(number),
      },
      {
        title: 'Verification Status',
        dataIndex: 'sso_verified',
        sorter: (a, b) => (a.sso_verified === 'Y') - (b.sso_verified === 'Y'),

        render: (status) => {
          const title = status === 'Y' ? 'Verified' : 'Unverified';
          const color = status === 'Y' ? 'green' : 'volcano';
          return (
            <span>
              <Tag color={color}>{title}</Tag>
            </span>
          );
        },
      },
      {
        title: 'Locked',
        dataIndex: 'locked',
        render: (status) => {
          return(
            <span>{status === true ? <i class="flaticon2-lock"></i> : ''}</span>
          )
        }
      },
      {
        title: 'Action',
        render: (text, record, index) =>
            <Link
              to={{
                pathname: `/pending-deletion/${record.sub}`,
                state: {
                  userObj: record,
                }
            }}>
              <button
                type="button"
                className="btn btn-link text-primary bg-transparent text-uppercase"
              >
                view/edit
              </button>
            </Link>
      },
    ]
    const dataIndex = ['given_name', 'family_name', 'address', 'email', 'phone_number', 'sso_verified'];

    return (
      <div className="row">
        <div className="col-lg-12 col-xl-12 order-lg-1 order-xl-1">
          <BulkActionsWorkflowModal />
          <Portlet config={portletConfig}>
            <PaginatedTable
              columns={columns}
              dataSource={users}
              rowKey={(record) => record.sub}
              rowSelection={rowSelection}
              currentPage={currentPage}
              getPage={getPage}
              loading={isFetching}
              limit={10}
              totalCount={totalCount}
              onColumnChange={sortColumns}
            />
            {isFetching && users.length > 0 &&
              <div className="text-center">
                <h4>Loading users...</h4>
              </div>
            }
          </Portlet>
          <CSVDownloadButton
            searchParams={searchParams}
            totalCount={totalCount}
            dataIndex={dataIndex}
            fetchType={'fetchUsers'}
            marked={true}
          />
        </div>
      </div>
    )
  }
}

export default UsersView;
